import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  Stack
} from '@mui/material';
import { FilterList as FilterListIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

export interface RecipeNutrition {
  calories: number;
  protein: string;
  carbs: string;
}

export interface Recipe {
  id: string;
  icon: string;
  name: string;
  category: string;
  servings: number;
  time: string;
  nutrition: RecipeNutrition;
  ingredients: string[];
  preparation: string;
  isSchedulable?: boolean;
}

interface RecipeDatabaseProps {
  recipes?: Recipe[];
  loading?: boolean;
  canSchedule?: boolean;
  onScheduleRecipe?: (recipe: Recipe) => void;
}

const DIETARY_RESTRICTIONS = ['vegetarian', 'vegan', 'halal', 'kosher', 'gluten-free', 'dairy-free', 'diabetic'];
const ALLERGENS = ['nuts', 'dairy', 'gluten', 'eggs', 'soy', 'shellfish', 'fish'];

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const STATIC_RECIPES: Recipe[] = [
  {
    id: 'ugali-sukuma',
    icon: '🥣',
    name: 'Fortified Ugali with Sukuma Wiki',
    category: 'Traditional',
    servings: 500,
    time: '45 min',
    nutrition: { calories: 420, protein: '15g', carbs: '65g' },
    ingredients: [
      'Iron-fortified maize flour - 125kg',
      'Fresh sukuma wiki - 85kg',
      'Cooking oil (Vitamin A) - 12L',
      'Onions - 15kg',
      'Salt (iodized) - 2kg'
    ],
    preparation: `PREPARATION (500 servings):
1. Boil 400L water in large pots
2. Gradually add 125kg fortified maize flour while stirring
3. Cook for 20-25 minutes until firm
4. Clean and chop 85kg sukuma wiki
5. Sauté onions in Vitamin A oil
6. Add sukuma wiki and cook 10 minutes
7. Season with iodized salt

NUTRITION per serving:
• Calories: 420 kcal
• Protein: 15g (fortified)
• Iron: 8mg (fortified)
• Vitamin A: 300mcg (oil)

COST: KSh 38 per serving`
  },
  {
    id: 'githeri-mixed',
    icon: '🍚',
    name: 'Githeri with Mixed Vegetables',
    category: 'High Protein',
    servings: 500,
    time: '90 min',
    nutrition: { calories: 510, protein: '22g', carbs: '68g' },
    ingredients: [
      'Maize (whole grain) - 100kg',
      'Red beans - 50kg',
      'Mixed vegetables - 40kg',
      'Beef/Chicken - 25kg',
      'Spices & seasoning - 3kg'
    ],
    preparation: `PREPARATION (500 servings):
1. Soak 100kg maize and 50kg beans overnight
2. Boil together for 60-90 minutes until tender
3. Add mixed vegetables in last 15 minutes
4. Brown 25kg meat separately
5. Combine all ingredients
6. Season with spices and salt

NUTRITION per serving:
• Calories: 510 kcal
• Protein: 22g (complete protein)
• Fiber: 12g
• Complex carbs: 68g

COST: KSh 42 per serving`
  },
  {
    id: 'fortified-uji',
    icon: '🥤',
    name: 'Fortified Porridge (Uji)',
    category: 'Breakfast',
    servings: 500,
    time: '30 min',
    nutrition: { calories: 450, protein: '18g', carbs: '75g' },
    ingredients: [
      'Fortified flour mix - 80kg',
      'Full cream milk powder - 15kg',
      'Sugar - 20kg',
      'Cooking fat - 8L',
      'Salt - 1kg'
    ],
    preparation: `PREPARATION (500 servings):
1. Mix 80kg fortified flour with cold water
2. Boil remaining water (350L)
3. Add flour mixture while stirring
4. Cook 15-20 minutes stirring constantly
5. Add milk powder and sugar
6. Add cooking fat for calories

NUTRITION per serving:
• Calories: 450 kcal
• Protein: 18g (milk protein)
• Vitamins: A, B-complex, D
• Minerals: Iron, Zinc, Calcium

COST: KSh 35 per serving`
  },
  {
    id: 'fish-stew',
    icon: '🐟',
    name: 'Fish Stew with Rice',
    category: 'High Omega-3',
    servings: 500,
    time: '60 min',
    nutrition: { calories: 620, protein: '28g', carbs: '72g' },
    ingredients: [
      'Fresh tilapia/sardines - 120kg',
      'Rice - 100kg',
      'Tomatoes - 30kg',
      'Coconut milk - 25L',
      'Spices (dhania, pilipili) - 2kg'
    ],
    preparation: `PREPARATION (500 servings):
1. Clean and cut 120kg fresh fish
2. Prepare rice (100kg) separately
3. Sauté onions and tomatoes
4. Add coconut milk and spices
5. Add fish and simmer 20 minutes
6. Serve over rice

NUTRITION per serving:
• Calories: 620 kcal
• Protein: 28g (high quality)
• Omega-3: 1.2g
• Healthy fats: 15g

COST: KSh 52 per serving`
  }
];

const mapApiRecipe = (recipe: any): Recipe => ({
  id: recipe.id,
  icon: recipe.metadata?.icon || '🍽️',
  name: recipe.recipeName || recipe.name || 'Unnamed recipe',
  category: recipe.category || 'Recipe',
  servings: Number(recipe.servingSize || recipe.yieldQuantity || 0),
  time: recipe.totalTimeMinutes != null ? `${recipe.totalTimeMinutes} min` : '--',
  nutrition: {
    calories: Number(recipe.caloriesPerServing || recipe.nutritionalInfo?.calories || 0),
    protein: `${recipe.nutritionalInfo?.protein || 0}g`,
    carbs: `${recipe.nutritionalInfo?.carbs || 0}g`,
  },
  ingredients: Array.isArray(recipe.ingredients)
    ? recipe.ingredients.map((ingredient: any) => (
      `${ingredient.ingredientName || ingredient.name} - ${ingredient.quantity ?? ''} ${ingredient.unitOfMeasure || ''}`.trim()
    ))
    : [],
  preparation: recipe.instructions || recipe.description || 'No preparation instructions recorded.',
  isSchedulable: true,
});

const RecipeDatabase: React.FC<RecipeDatabaseProps> = ({
  recipes,
  loading = false,
  canSchedule = false,
  onScheduleRecipe,
}) => {
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [selectedDietary, setSelectedDietary] = useState<string[]>([]);
  const [selectedAllergens, setSelectedAllergens] = useState<string[]>([]);
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[] | null>(null);
  const [filterLoading, setFilterLoading] = useState<boolean>(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<boolean>(false);

  const toggleDietary = (item: string): void => {
    setSelectedDietary(prev =>
      prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]
    );
  };

  const toggleAllergen = (item: string): void => {
    setSelectedAllergens(prev =>
      prev.includes(item) ? prev.filter(x => x !== item) : [...prev, item]
    );
  };

  const applyFilter = async (): Promise<void> => {
    setFilterLoading(true);
    setFilterError(null);
    try {
      const resp = await fetch(`${API}/api/v1/kitchen-ops/meal-planning/compatible-recipes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          dietaryRestrictions: selectedDietary,
          allergenExclusions: selectedAllergens
        })
      });
      if (!resp.ok) throw new Error(`Filter request failed: ${resp.status}`);
      const data = await resp.json();
      const list: Recipe[] = (Array.isArray(data) ? data : (data.data ?? [])).map(mapApiRecipe);
      setFilteredRecipes(list);
      setFilterActive(true);
    } catch (err: unknown) {
      const msg = (err as { message?: string }).message ?? 'Failed to fetch compatible recipes';
      setFilterError(msg);
    } finally {
      setFilterLoading(false);
    }
  };

  const clearFilters = (): void => {
    setSelectedDietary([]);
    setSelectedAllergens([]);
    setFilteredRecipes(null);
    setFilterActive(false);
    setFilterError(null);
  };

  const liveRecipes = recipes && recipes.length > 0 ? recipes : null;
  const displayRecipes: Recipe[] = filteredRecipes ?? liveRecipes ?? STATIC_RECIPES;

  const handleViewRecipe = (recipe: Recipe): void => {
    setSelectedRecipe(recipe);
  };

  const handleCloseRecipe = (): void => {
    setSelectedRecipe(null);
  };

  const handleScheduleRecipe = (recipe: Recipe): void => {
    if (recipe.isSchedulable && canSchedule && onScheduleRecipe) {
      onScheduleRecipe(recipe);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Recipe Database</Typography>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setFiltersOpen(prev => !prev)}
        >
          Filters {filterActive ? `(active)` : ''}
        </Button>
      </Box>

      {/* Filter Panel */}
      <Collapse in={filtersOpen}>
        <Card sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              Dietary Restrictions
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {DIETARY_RESTRICTIONS.map(item => (
                <Chip
                  key={item}
                  label={item}
                  clickable
                  color={selectedDietary.includes(item) ? 'primary' : 'default'}
                  onClick={() => toggleDietary(item)}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Stack>

            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
              Exclude Allergens
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {ALLERGENS.map(item => (
                <Chip
                  key={item}
                  label={item}
                  clickable
                  color={selectedAllergens.includes(item) ? 'error' : 'default'}
                  onClick={() => toggleAllergen(item)}
                  sx={{ textTransform: 'capitalize' }}
                />
              ))}
            </Stack>

            {filterError && (
              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setFilterError(null)}>
                {filterError}
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={applyFilter}
                disabled={filterLoading || (selectedDietary.length === 0 && selectedAllergens.length === 0)}
                startIcon={filterLoading ? <CircularProgress size={16} /> : undefined}
              >
                {filterLoading ? 'Filtering...' : 'Apply Filter'}
              </Button>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={!filterActive && selectedDietary.length === 0 && selectedAllergens.length === 0}
              >
                Clear Filters
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Collapse>

      {/* Active filter banner */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !liveRecipes && !filterActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No active backend recipes are available yet. Showing sample recipes for planning reference; create backend recipes before scheduling them.
        </Alert>
      )}

      {filterActive && filteredRecipes !== null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing {filteredRecipes.length} compatible recipe{filteredRecipes.length !== 1 ? 's' : ''} for selected dietary preferences
        </Alert>
      )}

      {/* Empty state after filter */}
      {!loading && filterActive && filteredRecipes !== null && filteredRecipes.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            border: '2px dashed #e0e0e0',
            borderRadius: 3,
            color: 'text.secondary'
          }}
        >
          <Typography variant="h6">No compatible recipes</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            No recipes match the selected dietary requirements.
          </Typography>
        </Box>
      ) : !loading && (
        <Grid container spacing={3}>
          {displayRecipes.map((recipe) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={recipe.id}>
              <Card
                sx={{
                  height: '100%',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'translateY(-5px)' }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        background: 'linear-gradient(135deg, #28a745, #20c997)',
                        fontSize: '1.5em',
                        mr: 2
                      }}
                    >
                      {recipe.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1em', mb: 0.5 }}>
                        {recipe.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {recipe.category} • {recipe.servings} servings • {recipe.time}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Box sx={{ backgroundColor: '#e8f5e8', p: 1, borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                          {recipe.nutrition.calories}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Calories</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ backgroundColor: '#e8f5e8', p: 1, borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                          {recipe.nutrition.protein}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Protein</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ backgroundColor: '#e8f5e8', p: 1, borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#28a745' }}>
                          {recipe.nutrition.carbs}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Carbs</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Key Ingredients:
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {recipe.ingredients.slice(0, 3).map((ingredient, index) => (
                        <ListItem key={index} sx={{ p: 0 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85em' }}>
                            • {ingredient}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleViewRecipe(recipe)}
                        sx={{
                          background: 'linear-gradient(135deg, #007bff, #0056b3)',
                          '&:hover': { background: 'linear-gradient(135deg, #0056b3, #004085)' }
                        }}
                      >
                        View Recipe
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        disabled={!recipe.isSchedulable || !canSchedule}
                        onClick={() => handleScheduleRecipe(recipe)}
                        sx={{
                          borderColor: '#6c757d',
                          color: '#6c757d',
                          '&:hover': { borderColor: '#545b62', backgroundColor: '#f8f9fa' }
                        }}
                      >
                        Schedule
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Recipe Detail Dialog */}
      <Dialog open={selectedRecipe !== null} onClose={handleCloseRecipe} maxWidth="md" fullWidth>
        {selectedRecipe && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    background: 'linear-gradient(135deg, #28a745, #20c997)',
                    fontSize: '2em'
                  }}
                >
                  {selectedRecipe.icon}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedRecipe.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRecipe.category} • {selectedRecipe.servings} servings • {selectedRecipe.time}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Ingredients:
                </Typography>
                <List>
                  {selectedRecipe.ingredients.map((ingredient, index) => (
                    <ListItem key={index}>
                      <Typography variant="body2">• {ingredient}</Typography>
                    </ListItem>
                  ))}
                </List>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Preparation & Nutrition:
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#f8f9fa',
                    p: 2,
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {selectedRecipe.preparation}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRecipe}>Close</Button>
              <Button
                onClick={() => { handleScheduleRecipe(selectedRecipe); handleCloseRecipe(); }}
                variant="contained"
                disabled={!selectedRecipe.isSchedulable || !canSchedule}
              >
                Schedule Recipe
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RecipeDatabase;
